import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
  inject
} from '@angular/core';
import { SwiperComponent } from 'swiper/angular';

// import Swiper core and required modules
import SwiperCore, {
  SwiperOptions,
  Virtual,
  Navigation,
  Pagination
} from 'swiper';
import {
  CoreActionUpdateUserLanguage,
  CoreQuickTourStepTypes,
  selectorCoreLogin
} from '@bepatient/core';
import { TranslateService } from '@ngx-translate/core';
import { SafeHtml } from '@angular/platform-browser';
import { ModalController } from '@ionic/angular';
import { Store, select } from '@ngrx/store';
import { Subscription } from 'rxjs';

// install Swiper modules
SwiperCore.use([Virtual, Pagination, Navigation]);

@Component({
  selector: 'bepatient-quick-tour',
  templateUrl: './quick-tour.component.html',
  styleUrls: ['./quick-tour.component.scss'],
  standalone: false
})
export class QuickTourComponent implements OnInit, AfterViewInit, OnDestroy {
  translate = inject(TranslateService);
  private modalCtrl = inject(ModalController);
  private store$ = inject<Store<any>>(Store);

  // Subscription
  subscription: Subscription;

  // Reference to video player
  @ViewChild('videoPlayer') videoPlayer: ElementRef;

  // Reference to swiper instance
  @ViewChild('quickTourSwiper', { static: false })
  quickTourSwiper?: SwiperComponent;
  safeHtml: SafeHtml;

  steps: Array<any> = [];

  // Swiper configuration
  swiperConfig: SwiperOptions = {
    slidesPerView: 2,
    spaceBetween: 50,
    navigation: {
      nextEl: '.swiper-button-next',
      prevEl: '.swiper-button-prev'
    },
    pagination: {
      el: '.quick-tour-pagination',
      clickable: true,
      enabled: true
    },
    allowTouchMove: true,
    virtual: {
      enabled: true,
      cache: true,
      addSlidesBefore: 3,
      addSlidesAfter: 3
    }
  };

  // All possible step types
  allQuickTourPages = [];

  // Current quick tour steps to be displayed in the swiper array
  quickTourPages: Array<any> = [];

  _activePageIndex = 0;

  get activePageIndex() {
    return this._activePageIndex;
  }

  set activePageIndex(index: number) {
    this._activePageIndex = index;
  }

  ngOnInit(): void {
    // Set the initial page to be displayed in the swiper
    this.subscription = this.store$
      .pipe(select(selectorCoreLogin))
      .subscribe(login => {
        const { language } = login;
        const rightToLeft = language ? language === 'ar' : false;
        this.store$.dispatch(
          new CoreActionUpdateUserLanguage({
            language,
            rightToLeft: rightToLeft
          })
        );
      });

    // Assigning tour pages data
    this.allQuickTourPages = [
      {
        type: CoreQuickTourStepTypes.INTRO,
        title: this.translate.instant('QUICK_TOUR.INTRO.TITLE'),
        subtitle: this.translate.instant('QUICK_TOUR.INTRO.SUBTITLE'),
        image: true
      },
      {
        type: CoreQuickTourStepTypes.TIMELINE,
        title: this.translate.instant('QUICK_TOUR.TIMELINE.TITLE'),
        subtitle: this.translate.instant('QUICK_TOUR.TIMELINE.SUBTITLE'),
        details: this.translate.instant('QUICK_TOUR.TIMELINE.DETAILS'),
        icon: 'timeline',
        animation: true
      },
      {
        type: CoreQuickTourStepTypes.MENU,
        title: this.translate.instant('QUICK_TOUR.MENU.TITLE'),
        subtitle: this.translate.instant('QUICK_TOUR.MENU.SUBTITLE'),
        details: this.translate.instant('QUICK_TOUR.MENU.DETAILS'),
        icon: 'menu',
        animation: true
      },
      {
        type: CoreQuickTourStepTypes.ACCOUNT,
        title: this.translate.instant('QUICK_TOUR.MY_ACCOUNT.TITLE'),
        subtitle: this.translate.instant('QUICK_TOUR.MY_ACCOUNT.SUBTITLE'),
        details: this.translate.instant('QUICK_TOUR.MY_ACCOUNT.DETAILS'),
        icon: 'account',
        animation: true
      }
    ];
    this.swiperInit();
  }

  ngAfterViewInit(): void {
    // this.playVideo();
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  onSlideChange(event) {
    if (event && event.length && event[0]) {
      // Do something after changing the slide
      // Set the current active index
      this.activePageIndex = event[0].activeIndex;
    }
  }

  swiperInit(): void {
    // Create a set of step types for efficient comparison
    const stepTypesSet = new Set(
      this.steps.map(step => CoreQuickTourStepTypes[step.toUpperCase()])
    );
    // Filter and map quick tour pages based on step types
    this.quickTourPages = this.allQuickTourPages
      .filter(page => stepTypesSet.has(page.type))
      .map(step => ({ ...step }));
  }

  nextSlide($event): void {
    this.quickTourSwiper.swiperRef.slideNext(500);
  }

  prevSlide($event): void {
    this.quickTourSwiper.swiperRef.slidePrev(500);
  }

  goToSlide(index: number): void {
    this.quickTourSwiper.swiperRef.slideTo(index, 500);
  }

  skipTour(index: number): void {
    // Saving info to local storage that tour has been skipped
    localStorage.setItem('quickTour', 'skipped');
    // Dismiss the modal
    this.dismissTour();
  }

  finishTour(index: number): void {
    // Saving info to local storage that tour has been completed
    localStorage.setItem('quickTour', 'done');
    // Dismiss the modal
    this.dismissTour();
  }

  getSVGIcon(iconName: string): any {
    switch (iconName) {
      case CoreQuickTourStepTypes.TIMELINE:
        return '/assets/icon/quick-tour-timeline.svg';

      case CoreQuickTourStepTypes.MENU:
        return '/assets/icon/quick-tour-menu.svg';

      case CoreQuickTourStepTypes.ACCOUNT:
        return '/assets/icon/quick-tour-account.svg';

      default:
        return false;
    }
  }

  dismissTour(): void {
    this.modalCtrl.getTop().then(() => {
      this.modalCtrl.dismiss();
    });
  }

  playVideo(): void {
    const video: HTMLVideoElement = this.videoPlayer.nativeElement;
    video.play();
  }

  pauseVideo(): void {
    const video: HTMLVideoElement = this.videoPlayer.nativeElement;
    video.pause();
  }
}
